"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateXlsx2Csv2Config = void 0;
const common_1 = require("@finley-technologies/common");
const tape_sanitizer_1 = require("@finley-technologies/tape-sanitizer");
const uinode_1 = require("./operations/sanitize/uinode");
const uinode_2 = require("./operations/xlsx2csv/uinode");
const generateXlsx2Csv2Config = ({ sanitizeNode, xlsx2csvNode, headers, }) => {
    (0, common_1.finleyAssert)(uinode_2.operation_xlsx2Csv.isNode(xlsx2csvNode), '!Xlsx2CsvOperation');
    (0, common_1.finleyAssert)(uinode_1.operation_sanitize.isNode(sanitizeNode), '!SanitizeOperation');
    const { baseFields, doNotSanitizeFieldNames } = sanitizeNode.configuration;
    switch (xlsx2csvNode.configuration.mode) {
        case 'convertRange': {
            const { sheetName, headerRange, dataEndRange, importRaw, formula, } = xlsx2csvNode.configuration;
            if (!headerRange) {
                return {
                    config: null,
                    errors: ['No header range in xlsx2csv'],
                };
            }
            // Legacy sanitize renamed every header (xlsx2csv turned newlines into
            // spaces, then sanitize snake_cased it unless doNotSanitizeFieldNames)
            // before looking up columnToReadFrom, so "Income " matched "Income".
            // xlsx2csv2 looks the header cell up as is (trimmed), so pin each field
            // to the file's exact header that legacy would have matched.
            const sanitizeNames = !doNotSanitizeFieldNames;
            const legacyHeaderKey = (header) => (0, tape_sanitizer_1.getSanitizedFieldName)(header.replace(/(\r\n|\n|\r)/gm, ' '), sanitizeNames);
            const errors = [];
            const resolveColumn = (column) => {
                if (!headers) {
                    return column;
                }
                const key = (0, tape_sanitizer_1.getSanitizedFieldName)(column, sanitizeNames);
                const matches = headers.filter((h) => legacyHeaderKey(h) === key);
                if (matches.length === 1) {
                    return matches[0];
                }
                errors.push(matches.length === 0
                    ? `[field: ${column}] matches no header in the sample file`
                    : `[field: ${column}] matches multiple headers in the sample file: [${matches.join(', ')}]`);
                return column;
            };
            const importFormat = importRaw ? 'raw' : 'formatted';
            const xlsx2csvConfig = {
                operation: 'xlsx2csv2',
                mode: 'convertRange',
                sheetName,
                isSheetNameRegex: true,
                headerRange,
                dataEndRange,
                fields: baseFields.map((baseField) => {
                    // sanitize
                    //   - reads from: baseField.columnToReadFrom ?? baseField.field
                    //   - writes to: getSanitizedFieldName(baseField.field, !doNotSanitizeFieldNames)
                    // when moving over the config to xlsx2csv2 we will make this explicit.
                    const columnToReadFrom = resolveColumn(baseField.columnToReadFrom ?? baseField.field);
                    const fieldToWriteTo = (0, tape_sanitizer_1.getSanitizedFieldName)(baseField.field, !doNotSanitizeFieldNames);
                    return {
                        importFormat: formula ? 'formula' : importFormat,
                        ...baseField,
                        columnToReadFrom,
                        field: fieldToWriteTo,
                    };
                }),
            };
            return {
                config: xlsx2csvConfig,
                errors,
            };
        }
        case 'extractValues': {
            const errors = [];
            const { extract } = xlsx2csvNode.configuration;
            const fields = extract.map((field) => {
                const { sheetName, fieldName, cellAddress, importRaw } = field;
                const importFormat = importRaw
                    ? 'raw'
                    : 'formatted';
                // find matching baseField from the baseFields definitions
                // normalize using getSanitizedFieldName
                const matchingBaseFields = baseFields.filter((bf) => {
                    const columnToReadFrom = bf.columnToReadFrom ?? bf.field;
                    return columnToReadFrom === fieldName;
                });
                if (matchingBaseFields.length === 0) {
                    // extracted by xlsx2csv but never read by sanitize, so sanitize
                    // dropped it too; dropping it here keeps the output the same.
                    errors.push(`[field: ${fieldName}] is extracted by xlsx2csv but not read by sanitize; dropped`);
                    return null;
                }
                if (matchingBaseFields.length > 1) {
                    errors.push(`multiple [${matchingBaseFields.length}] baseFields found for [fieldName:${fieldName}]`);
                    return null;
                }
                const baseField = matchingBaseFields[0];
                const fieldToWriteTo = (0, tape_sanitizer_1.getSanitizedFieldName)(baseField.field, !doNotSanitizeFieldNames);
                return {
                    ...baseField,
                    sheetName,
                    isSheetNameRegex: true,
                    cell: cellAddress,
                    importFormat,
                    field: fieldToWriteTo,
                    columnToReadFrom: fieldName,
                };
            });
            // read by sanitize but never extracted by xlsx2csv: sanitize emitted an
            // empty column for it (or failed if required); xlsx2csv2 has no cell to
            // read, so the column disappears from the output.
            const extracted = new Set(extract.map(({ fieldName }) => fieldName));
            baseFields
                .filter((bf) => !extracted.has(bf.columnToReadFrom ?? bf.field))
                .forEach((bf) => {
                errors.push(`[field: ${bf.columnToReadFrom ?? bf.field}] is read by sanitize but not extracted by xlsx2csv; its column is dropped`);
            });
            // sanitize wrote its columns in baseFields order, not extraction order.
            const outputOrder = (field) => baseFields.findIndex((bf) => (bf.columnToReadFrom ?? bf.field) === field.columnToReadFrom);
            const xlsx2csvConfig = {
                operation: 'xlsx2csv2',
                mode: 'extractValues',
                fields: fields
                    .filter(common_1.typeguards.isNotNullOrUndefined)
                    .sort((a, b) => outputOrder(a) - outputOrder(b)),
            };
            return {
                config: xlsx2csvConfig,
                errors,
            };
        }
        default:
            return {
                config: null,
                errors: ['Invalid xlsx2csv mode'],
            };
    }
};
exports.generateXlsx2Csv2Config = generateXlsx2Csv2Config;
//# sourceMappingURL=generateXlsx2Csv2Config.js.map